import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Standard connect-service response envelope. `listDevices` wraps its payload
 * in this and is unwrapped to `.data`; the mutating endpoints below (revoke,
 * logout-all, trust, reauth) are consumed for their success/failure only, so
 * their envelopes are left un-unwrapped.
 */
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
  error?: string;
}

/**
 * A session-bound device/browser known to the Keep Me Signed In feature.
 * `deviceId` is the stable identifier the revoke/trust endpoints key on;
 * `isCurrent` marks the device serving the active request (note the wire key
 * is `isCurrent`, not `current`).
 */
export interface Device {
  deviceId: string;
  label: string;
  lastUsedAt: string;
  isCurrent: boolean;
}

/**
 * Keep Me Signed In device-management API client. Session auth (cookie) and
 * the CSRF header are layered on by the root `authInterceptor` — calls here
 * set no manual `Authorization` header and do not touch CSRF themselves.
 * Targets `environment.apiUrl + '/user'`.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private baseUrl = environment.apiUrl + '/user';

  constructor(private http: HttpClient) {}

  public listDevices(): Observable<Device[]> {
    return this.http
      .get<ApiResponse<Device[]>>(`${this.baseUrl}/devices`)
      .pipe(map((r) => r.data));
  }

  public revokeDevice(deviceId: string): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/devices/${encodeURIComponent(deviceId)}`
    );
  }

  public logoutAllDevices(): Observable<any> {
    return this.http.post(`${this.baseUrl}/devices/logout-all`, {});
  }

  public trustCurrentDevice(): Observable<any> {
    return this.http.post(`${this.baseUrl}/devices/trust`, {});
  }

  public reauth(password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/reauth`, { password });
  }
}
